import { useState, useRef, useCallback, useEffect } from 'react';
import { INTERVIEW_MEDIA_CONFIG } from '@/components/interview/shared/InterviewMediaConfig';

interface UseMediaStreamOptions {
  onStreamChange?: (stream: MediaStream | null) => void;
  autoInitialize?: boolean;
}

export function useMediaStream({
  onStreamChange,
  autoInitialize = false
}: UseMediaStreamOptions = {}) {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isInitializingRef = useRef(false);
  const initializationPromiseRef = useRef<Promise<MediaStream> | null>(null);
  const [permissionsRevoked, setPermissionsRevoked] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setStream(null);
      onStreamChange?.(null);
    }
  }, [onStreamChange]);

  const initializeStream = useCallback(async () => {
    // If we already have a stream, return it
    if (streamRef.current) {
      return streamRef.current;
    }

    // If initialization is already in progress, return the existing promise
    if (isInitializingRef.current && initializationPromiseRef.current) {
      return initializationPromiseRef.current;
    }

    // Start initialization
    isInitializingRef.current = true;
    setPermissionsRevoked(false);

    initializationPromiseRef.current = navigator.mediaDevices
      .getUserMedia(INTERVIEW_MEDIA_CONFIG)
      .then(newStream => {
        streamRef.current = newStream;
        setStream(newStream);
        onStreamChange?.(newStream);

        // Add listeners to detect when tracks are ended (permissions revoked)
        newStream.getTracks().forEach(track => {
          track.addEventListener('ended', () => {
            console.warn(
              `Media track ${track.kind} ended - permissions may have been revoked`
            );
            setPermissionsRevoked(true);
            // Clean up the stream since it's no longer usable
            stopStream();
          });
        });

        return newStream;
      })
      .finally(() => {
        isInitializingRef.current = false;
        initializationPromiseRef.current = null;
      });

    return initializationPromiseRef.current;
  }, [onStreamChange, stopStream]);

  // Auto-initialize if requested
  useEffect(() => {
    if (autoInitialize && !streamRef.current && !isInitializingRef.current) {
      initializeStream().catch(error => {
        console.error('Failed to initialize media stream:', error);
      });
    }
  }, [autoInitialize, initializeStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  return {
    stream,
    streamRef,
    initializeStream,
    stopStream,
    isInitializing: isInitializingRef.current,
    permissionsRevoked
  };
}
